import { DefaultButton, Icon, Image, ImageFit, Link, Modal, PrimaryButton, Stack, Text, useTheme } from "@fluentui/react";
import React, { useState } from "react";
import { getResources } from "../../../app/getResources";
import { Measures } from "../../../telemetry/Measures";
import { dashboard } from "../../../telemetry/TraceLogging";
import { getActionIconStackStyle, getActionIconStyle, getActionInfoDescriptionStyles, getActionTitleStyles, getImageIconStyle } from "./Actions/EvolvedSettingsAction.styles";
import EvolvedSettingsInfoButton from "./Actions/EvolvedSettingsInfoButton";
import EvolvedSettingsToggle from "./Actions/EvolvedSettingsToggleSetting";
import { addPreserveWhitespaceStyle, getDisplayFlex, getHideConsentButtonAreaStyles, getHideConsentButtonStyles, getHideConsentDialogContentStyles, getHideConsentDialogSubTitleStyles, getHideConsentDialogTitleStyles, getHideConsentModalDialogStyles } from "./EvolvedSettingsDialog.styles";
const EvolvedSettingsHideConsentModal = React.memo(({ isEnablingFeedProvider, isBoardsLiteEnabled, feedProviderModelRegistration, onConsent, onCancel, }) => {
    const theme = useTheme();
    let consentButtonProps = {
        onClick: () => {
            onConsent(feedProviderModelRegistration);
        },
    };
    let cancelButtonProps = {
        onClick: () => {
            onCancel(feedProviderModelRegistration);
        },
    };
    let tokens = {
        childrenGap: "9px",
    };
    return React.createElement(Modal, { isOpen: true, isBlocking: true, styles: getHideConsentModalDialogStyles(theme), onDismiss: onCancel },
        React.createElement(Stack.Item, { styles: getHideConsentDialogContentStyles() },
            React.createElement(Text, { styles: getDisplayFlex(getHideConsentDialogTitleStyles()) }, getResources().SettingsEvolvedDialog_ShowFeeds_CloseConsentPopup_Title),
            React.createElement(Text, { styles: getDisplayFlex(getHideConsentDialogSubTitleStyles()) }, isBoardsLiteEnabled
                ? (isEnablingFeedProvider
                    ? getResources().SettingsEvolvedDialog_ShowBoards_CloseConsentPopup_TurningOnBoardsText
                    : getResources().SettingsEvolvedDialog_ShowBoards_CloseConsentPopup_TurningOffBoardsText)
                : (isEnablingFeedProvider
                    ? getResources().SettingsEvolvedDialog_ShowFeeds_CloseConsentPopup_TurningOnFeedsText
                    : getResources().SettingsEvolvedDialog_ShowFeeds_CloseConsentPopup_TurningOffFeedsText))),
        React.createElement(Stack.Item, { styles: getHideConsentButtonAreaStyles(theme) },
            React.createElement(Stack, { horizontal: true, tokens: tokens },
                React.createElement(PrimaryButton, Object.assign({}, consentButtonProps, { styles: getHideConsentButtonStyles() }), isEnablingFeedProvider
                    ? getResources().SettingsEvolvedDialog_ShowFeeds_CloseConsentPopup_TurnOnButtonText
                    : getResources().SettingsEvolvedDialog_ShowFeeds_CloseConsentPopup_TurnOffButtonText),
                React.createElement(DefaultButton, Object.assign({}, cancelButtonProps, { styles: getHideConsentButtonStyles() }), getResources().SettingsEvolvedDialog_ShowFeeds_CloseConsentPopup_CancelText))));
});
// Settings Page that offers the Show Or Hide Settings options.
const EvolvedSettingsShowOrHidePage = React.memo(({ is3pPlatformFeedsEnabled, isBoardsLiteEnabled, onLinkInvoked, feedProviderModelRegistrations, feedModelApi, launchStoreToFeeds }) => {
    const theme = useTheme();
    const [isCloseConsentOpen, setIsCloseConsentOpen] = useState(false);
    const [isEnablingFeedProvider, setIsEnablingFeedProvider] = useState(false);
    const [feedProviderInteracting, setFeedProviderInteracting] = useState();
    const [toggleKey, setToggleKey] = useState(0);
    // Setting a cap of 1000 to avoid overflow
    const updateToggleKey = (prevKey) => prevKey >= 1000 ? 0 : prevKey + 1;
    function isDisablingLastProvider(feedProvider) {
        return feedProvider.enabled &&
            (1 === (feedProviderModelRegistrations === null || feedProviderModelRegistrations === void 0 ? void 0 : feedProviderModelRegistrations.filter((value) => {
                return value.enabled;
            }).length));
    }
    function isEnablingFirstProvider(feedProvider) {
        return !feedProvider.enabled &&
            !(feedProviderModelRegistrations === null || feedProviderModelRegistrations === void 0 ? void 0 : feedProviderModelRegistrations.some((value) => {
                return value.enabled;
            }));
    }
    async function onFeedProviderToggleInteracted(feedProvider) {
        setFeedProviderInteracting(feedProvider);
        if (isDisablingLastProvider(feedProvider)) {
            setIsEnablingFeedProvider(false);
            setIsCloseConsentOpen(true);
        }
        else if (isEnablingFirstProvider(feedProvider)) {
            setIsEnablingFeedProvider(true);
            setIsCloseConsentOpen(true);
        }
        else {
            toggleFeedProvider(feedProvider);
        }
    }
    async function toggleFeedProvider(feedProvider) {
        // Calling a change to set the pivot to none, when we know this will result in a non-pivot situation.
        if (isDisablingLastProvider(feedProvider)) {
            feedModelApi.setSelectedFeedPivotHelper("");
        }
        // Awaited to ensure that this telemetry is sent before the telemetry triggered by the following API call
        await Measures.logEvent(dashboard, "FeedsProviderEnableFeedProviderWebApp", 33554432 /* productAndServiceUsage */, {
            feedProviderId: feedProvider.id,
            isEnabled: !feedProvider.enabled,
        });
        feedModelApi.setFeedProviderEnabledStateAsync(feedProvider.id, !feedProvider.enabled);
    }
    async function onToggleFeedProviderConsented(feedProvider) {
        if (feedProvider) {
            // Awaited to ensure that this telemetry is sent before the telemetry triggered by the following API call
            await Measures.logEvent(dashboard, "ConsentedToHideHostWindowWebApp", 33554432 /* productAndServiceUsage */, {
                isEnabling: !feedProvider.enabled,
                providerId: feedProvider.id,
            });
            toggleFeedProvider(feedProvider);
        }
        setToggleKey(prevKey => updateToggleKey(prevKey));
        setIsCloseConsentOpen(false);
    }
    async function onToggleFeedProviderCancelled(feedProvider) {
        setToggleKey(prevKey => updateToggleKey(prevKey));
        setIsCloseConsentOpen(false);
    }
    if (!!feedProviderModelRegistrations) {
        if (feedProviderModelRegistrations.length > 0) {
            let showOrHideContent = [];
            feedProviderModelRegistrations.forEach((feedProvider) => {
                if (feedProvider.canBeDisabled) {
                    let icon = React.createElement(Image, { src: feedProvider.icon, imageFit: ImageFit.contain, styles: getImageIconStyle() });
                    showOrHideContent.push(React.createElement(EvolvedSettingsToggle, { key: 
                        // isChecked is linked to feedProvider.enabled, but this is not updated in the cancelled scenario
                        // isChecked is artificially updated to !isChecked onToggle event, so toggle state doesn't match feedProvider.enabled anymore, and the actual feedProvider.enabled stays the same till consent happens
                        // as cancelled doesn't change feedProvider.enabled state, nothing changes in this component and no re-render is triggered.
                        // Using toggleKey as a variable to artificially modify _something_ in the component to be able to trigger a re-render
                        // toggleKey doesn't mean anything and it's just a mean to trigger a re-render
                        `${toggleKey}-${feedProvider.displayName}`, icon: icon, title: feedProvider.displayName, isChecked: feedProvider.enabled, onClick: () => {
                            onFeedProviderToggleInteracted(feedProvider);
                        }, role: "listitem" }));
                }
            });
            if (is3pPlatformFeedsEnabled && !isBoardsLiteEnabled) {
                const linkStringSplit = getResources().SettingsEvolvedDialog_ShowFeeds_AddMoreFeedsFrom.split("{feedsstorepagelink}");
                const linkPrefix = linkStringSplit[0];
                const linkSuffix = linkStringSplit[1];
                const linkKeyboardHandler = (ev) => {
                    if (ev.key === " " || ev.key === "Enter") {
                        launchStoreToFeeds();
                    }
                };
                showOrHideContent.push(React.createElement(Text, { styles: addPreserveWhitespaceStyle(getDisplayFlex(getActionInfoDescriptionStyles(theme))) },
                    linkPrefix,
                    React.createElement(Link, { tabindex: 0, onKeyPress: linkKeyboardHandler, onClick: launchStoreToFeeds, as: "a" }, getResources().SettingsEvolvedDialog_ShowFeeds_MicrosoftStore),
                    linkSuffix));
            }
            return React.createElement(React.Fragment, null,
                isCloseConsentOpen &&
                    React.createElement(EvolvedSettingsHideConsentModal, { feedProviderModelRegistration: feedProviderInteracting, isEnablingFeedProvider: isEnablingFeedProvider, isBoardsLiteEnabled: isBoardsLiteEnabled, onConsent: onToggleFeedProviderConsented, onCancel: onToggleFeedProviderCancelled }),
                showOrHideContent);
        }
        else {
            if (is3pPlatformFeedsEnabled) {
                return React.createElement(EvolvedSettingsInfoButton, { content: React.createElement(Text, { styles: getDisplayFlex(getActionTitleStyles(theme)) }, isBoardsLiteEnabled
                        ? getResources().SettingsEvolvedDialog_ShowBoards_AddBoardToStartPersonalizingText
                        : getResources().SettingsEvolvedDialog_ShowFeeds_AddFeedToStartPersonalizingText), onClick: launchStoreToFeeds, actionNode: React.createElement(Stack, { styles: getActionIconStackStyle() },
                        React.createElement(Icon, { iconName: "SegoeFluentOpenInNewWindow", styles: getActionIconStyle(theme) })) });
            }
        }
    }
    return React.createElement(React.Fragment, null);
});
export default EvolvedSettingsShowOrHidePage;
